/**
 * What a garage mutation actually *did*, worked out from the two overviews
 * either side of it.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * Every spend used to confirm itself with a verb and a price — `Upgraded`, over
 * `300 credits withdrawn`. Both halves of that are things the player already
 * knew: they pressed Upgrade, and the button had the price printed on it. The
 * half nobody was told is the half they paid for. Worse, it is the half that
 * becomes *unknowable* the moment the answer lands: every mutation replaces the
 * whole overview, so the card behind the banner has already redrawn with the new
 * figures and there is no "before" left anywhere on screen to compare them
 * against. A player who upgrades a turret three times in a row has watched six
 * numbers move and can name none of them.
 *
 * So this module answers the other question — *and what did I get* — for the
 * one moment it can still be answered: while both overviews are in hand.
 *
 * ── Read off the server's two answers, never off the card ────────────────────
 *
 * The same rule `spendPhrase` was written under. The client is holding what the
 * server said before the call and what it said after, and the difference between
 * those two is not an opinion. A figure computed from a card's own price, level
 * or stat block is a second opinion that can differ from the ledger — and the
 * cases where it differs (a boosted price, a hull part scaled by the hull it is
 * bolted to, a purchase the server auto-applied) are exactly the cases where a
 * player is most likely to look.
 *
 * ── Pure, and it does not know what a banner is ──────────────────────────────
 *
 * No Vue, no store, no `showNotice`. It takes two overviews and an id and
 * returns plain data, which is what makes `garage-outcome.test.ts` possible
 * without a browser or a server — the same shape `confirm-purchase.ts` and
 * `daily-quest-box.ts` are in. The formatting *is* part of the answer rather
 * than something the banner does later, because `ui/NotificationBanner.vue` must
 * stay unable to ask which caller sent a notice; see the note at the top of
 * `state/notification-banner.ts`.
 */
import type { GarageItem, GarageOverview } from '@/net/api';
import type { BannerStat } from './notification-banner';
import {
  HULL_STATS,
  SPEC_STATS,
  TURRET_STATS,
  compare,
  fillFraction,
  formatCredits,
  formatStat,
  specValues,
  type StatDescriptor,
} from '@/garage/stat-display';

/** Everything a mutation is worth saying, beyond the verb the caller chose. */
export interface Outcome {
  /**
   * The lines under the title, in the order they are shown: what the mutation
   * produced, then what it paid out, then what it cost.
   */
  lines: string[];
  /** Before-and-after bars. Empty for the mutations that move no stat. */
  stats: BannerStat[];
}

/**
 * At most this many rows.
 *
 * A hull upgrade moves all six of its stats, which is a readable block. A
 * *bundle* replaces the whole tank and would move twelve — a banner tall enough
 * to cover the shelf it is confirming a purchase from. The cut is stated rather
 * than left to the layout, and it takes the **largest** moves, so what falls off
 * the end is always the least of it.
 */
const MAX_STAT_ROWS = 6;

/**
 * The whole answer: what changed, in the order it is worth reading.
 *
 * `id` is whatever the mutation was keyed on — an item id, a research node id, a
 * hull part id, or one of the synthetic keys the shop uses for an amount
 * (`convert_400`). Anything it does not recognise simply contributes no subject
 * line, which is the honest degradation: the gains and the spend are still read
 * off the balances, and those are true whatever was pressed.
 */
export function describeOutcome(
  id: string,
  before: GarageOverview | null,
  after: GarageOverview,
): Outcome {
  if (!before) return { lines: [], stats: [] };

  const lines: string[] = [];
  const subject = subjectLine(id, before, after);
  if (subject) lines.push(subject);

  const gained = gainPhrase(before, after);
  if (gained) lines.push(gained);

  const spent = spendPhrase(before, after);
  if (spent) lines.push(spent);

  return { lines, stats: statChanges(id, before, after) };
}

/**
 * The name of the thing that was acted on, and what it now is.
 *
 * Three lookups in priority order, because one id space is shared by three
 * tables and an id that appears in two of them is a catalogue bug rather than an
 * ambiguity to resolve here.
 *
 * The level suffix is the part that took a second pass to get right. `Upgraded`
 * with the item's name under it is better than `Upgraded` alone, but it still
 * leaves the player counting pips on the card to find out where they got to —
 * and the pips are the thing that just changed. So an item whose level moved
 * says so in words.
 */
function subjectLine(
  id: string,
  before: GarageOverview,
  after: GarageOverview,
): string | null {
  const node = after.research.find((n) => n.id === id);
  if (node) return node.name;

  const item = after.items.find((i) => i.id === id);
  if (item) {
    const was = before.items.find((i) => i.id === id) ?? null;
    return `${item.name}${levelSuffix(was, item)}`;
  }

  const part = partName(id, after) ?? partName(id, before);
  if (part) return part;

  // A bundle, or anything else that grants rather than changes: name what
  // arrived. Bounded to three, because a grant of everything is a grant nobody
  // reads.
  const granted = after.items
    .filter((i) => i.owned && !before.items.find((b) => b.id === i.id)?.owned)
    .map((i) => i.name);
  if (granted.length > 0 && granted.length <= 3) return granted.join(', ');
  if (granted.length > 3) return `${granted.length} items added`;

  return null;
}

/**
 * ` — level 3 of 5`, or nothing.
 *
 * Top tier is counted separately from the credit ladder because the server does:
 * they are different currencies buying different columns, and rolling them into
 * one figure would report a tank as being past a maximum it never passed.
 */
function levelSuffix(was: GarageItem | null, now: GarageItem): string {
  if (was && now.prestige > was.prestige) {
    return ` — top tier ${now.prestige} of ${now.maxPrestigeLevel}`;
  }
  if (was && now.level > was.level) {
    return ` — level ${now.level} of ${now.maxUpgradeLevel}`;
  }
  return '';
}

/** A hull part's name, from whichever hull's window came down with the overview. */
function partName(id: string, overview: GarageOverview): string | null {
  for (const slot of overview.customise?.slots ?? []) {
    const part = slot.parts.find((p) => p.id === id);
    if (part) return part.name;
  }
  return null;
}

/**
 * What a mutation actually cost, as a phrase, or `null` when nothing moved.
 *
 * **Taken from the balances either side of the call, not from the price the
 * client quoted.** The server decides what to charge — it picks the currency
 * from the item's own `diamondPrice`, applies boosts, and scales a hull part by
 * the hull it is being fitted to — so a figure computed here from a card's price
 * is a second opinion that can differ from the ledger. The difference of two
 * balances the server sent cannot.
 *
 * Only **decreases** are reported. `mutate` also carries the equips, which cost
 * nothing, and the conversion, which moves one balance up and the other down; a
 * player asking "what did that take" wants the half that left.
 *
 * Words rather than the drawn currency mark, because a notice is a plain string —
 * the same convention `askToBuy`'s prose follows for exactly this reason.
 */
export function spendPhrase(before: GarageOverview, after: GarageOverview): string | null {
  const parts: string[] = [];
  const credits = before.player.credits - after.player.credits;
  const diamonds = before.wallet.diamonds - after.wallet.diamonds;
  if (credits > 0) parts.push(`${formatCredits(credits)} credits`);
  if (diamonds > 0) parts.push(`${formatCredits(diamonds)} diamonds`);
  return parts.length > 0 ? `${parts.join(' and ')} withdrawn` : null;
}

/**
 * The mirror of the above: what arrived.
 *
 * Four balances rather than two, because the things a player *buys* with money
 * in this garage are not all money. Fuel and experience are bought outright from
 * the shop, and a refill that says only "20 diamonds withdrawn" has reported the
 * transaction backwards.
 *
 * XP is only ever worth stating when it was bought. It also arrives from a
 * sortie and from a quest claim, and both of those are already announced by the
 * screen that owns them — but neither goes through `mutate`, so neither reaches
 * this function, and the overlap is theoretical rather than a case to guard.
 */
export function gainPhrase(before: GarageOverview, after: GarageOverview): string | null {
  const parts: string[] = [];
  const credits = after.player.credits - before.player.credits;
  const diamonds = after.wallet.diamonds - before.wallet.diamonds;
  const fuel = after.wallet.fuel - before.wallet.fuel;
  const xp = after.wallet.xp - before.wallet.xp;
  if (credits > 0) parts.push(`${formatCredits(credits)} credits`);
  if (diamonds > 0) parts.push(`${formatCredits(diamonds)} diamonds`);
  if (fuel > 0) parts.push(`${formatCredits(fuel)} fuel`);
  if (xp > 0) parts.push(`${formatCredits(xp)} XP`);
  return parts.length > 0 ? `${parts.join(' and ')} credited` : null;
}

/**
 * Which numbers moved, and how far.
 *
 * **Two sources, and which one is right depends on what was pressed.** An item's
 * own `currentStats` is the right answer for an upgrade or a top-tier step,
 * because those can be bought for a hull sitting on the shelf that the player is
 * not driving — the resolved spec would not move at all and the banner would
 * report a purchase as having changed nothing. A hull *part*, on the other hand,
 * has no row in `items`: it is bolted to one chassis and its whole effect is on
 * the resolved spec, so that is where it has to be read.
 *
 * Equipping is the case that falls out for free and is worth stating, because it
 * looked like it needed a branch. A fit finds its item, finds `currentStats`
 * unchanged — fitting a turret does not alter the turret — and produces no rows,
 * which is what a fit should say. The stat block belongs to the purchase, not to
 * the act of putting it on.
 */
function statChanges(
  id: string,
  before: GarageOverview,
  after: GarageOverview,
): BannerStat[] {
  const now = after.items.find((i) => i.id === id);
  if (now) {
    const was = before.items.find((i) => i.id === id);
    if (!was) return [];
    const descriptors =
      now.kind === 'HULL' ? HULL_STATS : now.kind === 'TURRET' ? TURRET_STATS : [];
    return rowsFor(descriptors, was.currentStats, now.currentStats);
  }

  // Not an item, so the only thing that can have moved is the tank being driven.
  return rowsFor(
    SPEC_STATS,
    specValues(before.spec as unknown as Record<string, unknown>),
    specValues(after.spec as unknown as Record<string, unknown>),
  );
}

/**
 * The formatting pass, and the only place a bar fraction is decided.
 *
 * Unchanged stats are dropped rather than drawn flat: a row that says `24 → 24`
 * is noise in a strip that has a few seconds of the player's attention, and the
 * rows that did move are the whole message. A stat missing from either side is
 * dropped too — an absent number is not a change from zero, and drawing it as
 * one would report a catalogue gap as a collapse in performance.
 */
function rowsFor(
  descriptors: readonly StatDescriptor[],
  was: Record<string, number>,
  now: Record<string, number>,
): BannerStat[] {
  const rows: Array<BannerStat & { magnitude: number }> = [];
  for (const descriptor of descriptors) {
    const from = was[descriptor.key];
    const to = now[descriptor.key];
    if (typeof from !== 'number' || typeof to !== 'number') continue;
    const direction = compare(descriptor, to, from);
    if (direction === 0) continue;
    rows.push({
      label: descriptor.label,
      from: formatStat(descriptor, from),
      to: formatStat(descriptor, to),
      fromFill: fillFraction(descriptor, from),
      toFill: fillFraction(descriptor, to),
      better: direction > 0,
      // Compared as a *fraction of the bar* rather than in the stat's own unit,
      // or "range" — measured in hundreds of metres — would win every ranking
      // against armour, which is measured in tenths.
      magnitude: Math.abs(fillFraction(descriptor, to) - fillFraction(descriptor, from)),
    });
  }
  return rows
    .sort((a, b) => b.magnitude - a.magnitude)
    .slice(0, MAX_STAT_ROWS)
    .map(({ magnitude: _magnitude, ...row }) => row);
}
